import { useEffect, useRef, useCallback } from 'react';

import { useQueryClient } from '@tanstack/react-query';

import { getAccessToken } from './authToken';

const BACKEND = process.env.REACT_APP_BACKEND_WORKER_URL;
const RECONNECT_DELAYS = [1000, 2000, 4000, 8000, 16000, 30000];
const SKIP_EVENTS = new Set(['connected', 'timeout']);

/**
 * Parses a single SSE message block into { eventType, data }.
 * Returns null if the message has no data or should be skipped.
 */
function parseSseMessage(message) {
	if (!message.trim()) return null;

	let eventType = 'message';
	let data = null;

	for (const line of message.split('\n')) {
		if (line.startsWith('event: ')) {
			eventType = line.slice(7);
		} else if (line.startsWith('data: ')) {
			try {
				data = JSON.parse(line.slice(6));
			} catch {
				data = line.slice(6);
			}
		}
	}

	if (data === null || SKIP_EVENTS.has(eventType)) return null;
	return { eventType, data };
}

/**
 * Processes a chunk of SSE data: splits into messages, parses each,
 * and dispatches callbacks / query invalidations.
 *
 * @returns {string} remaining buffer (incomplete message)
 */
function processSseChunk(buffer, { onEvent, invalidateKeys, queryClient }) {
	const messages = buffer.split('\n\n');
	const remaining = messages.pop(); // Keep incomplete message in buffer

	for (const raw of messages) {
		const parsed = parseSseMessage(raw);
		if (!parsed) continue;

		if (onEvent) onEvent(parsed.eventType, parsed.data);

		if (invalidateKeys) {
			for (const key of invalidateKeys) {
				queryClient.invalidateQueries({ queryKey: key });
			}
		}
	}

	return remaining;
}

/**
 * Custom hook for Server-Sent Events with JWT authentication.
 *
 * Uses fetch() with ReadableStream because native EventSource does not
 * support custom Authorization headers.
 *
 * @param {string[]} topics - e.g. ["leads:abc-123", "analyses:abc-123"]
 * @param {object} options
 * @param {boolean} options.enabled - whether to open connection (default: true)
 * @param {function} options.onEvent - callback for each event: (eventType, data) => void
 * @param {string[][]} options.invalidateKeys - React Query keys to invalidate on any event
 */
export function useServerEvents(topics, { enabled = true, onEvent, invalidateKeys } = {}) {
	const queryClient = useQueryClient();
	const abortRef = useRef(null);
	const retryCountRef = useRef(0);
	const retryTimerRef = useRef(null);

	// Keep refs to onEvent/invalidateKeys so the SSE connection isn't torn down and
	// rebuilt every render just because callers pass inline arrow functions.
	const onEventRef = useRef(onEvent);
	const invalidateKeysRef = useRef(invalidateKeys);
	onEventRef.current = onEvent;
	invalidateKeysRef.current = invalidateKeys;

	const topicsKey = topics?.join(',');

	const connect = useCallback(async () => {
		if (!topicsKey) return;

		const token = await getAccessToken();
		if (!token) return;

		const controller = new AbortController();
		abortRef.current = controller;

		const url = `${BACKEND}/api/v1/platform/events/stream?topics=${encodeURIComponent(topicsKey)}`;

		try {
			const response = await fetch(url, {
				headers: { Authorization: `Bearer ${token}` },
				signal: controller.signal,
			});

			if (!response.ok) {
				throw new Error(`SSE connection failed: ${response.status}`);
			}

			retryCountRef.current = 0;

			const reader = response.body.getReader();
			const decoder = new TextDecoder();
			let buffer = '';

			// eslint-disable-next-line no-constant-condition
			while (true) {
				const { done, value } = await reader.read();
				if (done) break;

				buffer += decoder.decode(value, { stream: true });
				buffer = processSseChunk(buffer, {
					onEvent: onEventRef.current,
					invalidateKeys: invalidateKeysRef.current,
					queryClient,
				});
			}
		} catch (err) {
			if (err.name === 'AbortError') return;

			const delay = RECONNECT_DELAYS[Math.min(retryCountRef.current, RECONNECT_DELAYS.length - 1)];
			retryCountRef.current += 1;

			retryTimerRef.current = setTimeout(() => {
				connect();
			}, delay);
		}
	}, [topicsKey, queryClient]);

	useEffect(() => {
		if (!enabled || !topicsKey) {
			if (abortRef.current) {
				abortRef.current.abort();
				abortRef.current = null;
			}
			return;
		}

		connect();

		return () => {
			if (abortRef.current) {
				abortRef.current.abort();
				abortRef.current = null;
			}
			if (retryTimerRef.current) {
				clearTimeout(retryTimerRef.current);
				retryTimerRef.current = null;
			}
		};
	}, [enabled, connect, topicsKey]);
}
